import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import useUploadToS3 from 'src/hooks/useUploadToS3';
import { Avatar, Button, ProgressBar } from 'src/shared-components';
import LoadingOverlay from 'src/shared-components/Loading/LoadingOverlay';

const DEFAULT_USER_AVATAR = process.env.DEFAULT_USER_AVATAR || '';

const FormProfilePicture = ({
  photoRoundness = 'full',
  photoUrl,
  onFileUploaded,
  onRemovePhotoUrl,
  flexStyle,
}: {
  photoRoundness?: 'md' | 'full';
  photoUrl: string;
  onFileUploaded: (url: string) => void;
  onRemovePhotoUrl: () => void;
  flexStyle?: boolean;
}) => {
  const [currentPhotoUrl, setCurrentPhotoUrl] = useState<string>(photoUrl);
  const { upload, url, error, progress, status } = useUploadToS3();

  const handleFileSelected = (file: File) => {
    upload(file, file.type);
  };

  useEffect(() => {
    if (url) {
      // TODO: try to mock the upload to S3
      /* istanbul ignore next */

      setCurrentPhotoUrl(url);
      onFileUploaded(url);
    }
  }, [url]);

  useEffect(() => {
    if (error) {
      // TODO: show error message to user
      /* istanbul ignore next */
      console.error(error);
    }
  }, [error]);

  useEffect(() => {
    setCurrentPhotoUrl(photoUrl);
  }, [photoUrl]);

  return (
    <>
      {status === 'pending' && <LoadingOverlay />}
      <div data-testid="form-profile-picture">
        <div className={`${flexStyle && 'flex'}`}>
          <CustomInput
            dataTestId="drop-input"
            photoRoundness={photoRoundness}
            photoUrl={currentPhotoUrl}
            isDragZone={true}
            onFileSelected={handleFileSelected}
            imageSize={`${flexStyle ? 'md' : 'lg'}`}
          />
          <div className={`${flexStyle ? 'flex flex-col ml-6 gap-y-2' : 'grid grid-cols-6 gap-4 mt-9 mb-3'}`}>
            <div className="col-span-6 sm:col-span-3">
              <CustomInput onFileSelected={handleFileSelected} />
            </div>
            <div className="col-span-6 sm:col-span-3">
              <Button
                data-testid="removePhotoButton"
                size="lg"
                roundness="md"
                color="white"
                fullWidth
                onClick={() => onRemovePhotoUrl()}
                borderColor="white"
              >
                Remove photo
              </Button>
            </div>
          </div>
        </div>
        {progress > 0 && progress < 100 && <ProgressBar progress={progress} />}
      </div>
    </>
  );
};

const CustomInput = ({
  isDragZone = false,
  photoRoundness = 'full',
  photoUrl,
  dataTestId = '',
  onFileSelected,
  imageSize = 'lg',
}: {
  isDragZone?: boolean;
  photoRoundness?: 'md' | 'full';
  photoUrl?: string;
  dataTestId?: string;
  onFileSelected: (file: File) => void;
  imageSize?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg';
}) => {
  const onDrop = useCallback(acceptedFiles => {
    onFileSelected(acceptedFiles[0]);
  }, []);
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: 'image/jpeg, image/png',
    multiple: false,
  });
  return (
    <div {...getRootProps()} className="flex justify-center">
      <input data-testid={dataTestId} {...getInputProps()} />
      {isDragZone ? (
        <Avatar
          image={photoUrl || DEFAULT_USER_AVATAR}
          size={imageSize}
          roundness={photoRoundness}
          className={`cursor-pointer ${imageSize === 'md' && 'rounded-[36px]'}`}
        />
      ) : (
        <Button size="lg" roundness="md" color="secondary" fullWidth className="px-12">
          Upload photo
        </Button>
      )}
    </div>
  );
};

export default FormProfilePicture;
